import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Channel, VeevaSyncStatus } from 'modules/maya/basics/enums/maya.enums';
import useTacticCard from 'modules/maya/components/TacticCard/TacticCard.hook';
import {
  CustomContentCardStyle,
  TacticCardFontStyle,
  TacticCardTitleStyle,
  clickableIdStyle,
  iconsStyle,
} from 'modules/maya/components/TacticCard/TacticCard.styles';
import { TacticCardType } from 'modules/maya/components/TacticCard/TacticCard.types';
import { ButtonBackgroundType, ButtonVariant, IconButton } from 'yoda-ui/BiogenLincDS/Buttons';
import Box from 'yoda-ui/Box';
import Card from 'yoda-ui/Card/Card';
import ConfirmationDialog from 'yoda-ui/Dialogs/ConfirmationDialog';
import { Icons } from 'yoda-ui/Icons/Icon';
import Loader from 'yoda-ui/Loader';
import { YodaColors, YodaSpacing } from 'yoda-ui/yodaTheme';

const TacticCard: FC<TacticCardType> = ({ tactic, readOnly }) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const {
    deleteTacticCallback,
    editTacticCallback,
    getCategoryForChannel,
    getStyleFromCategory,
    getStyleFromVeevaSyncStatus,
    handleCopy,
    open,
    openModalCallback,
    removeTacticLoading,
    setOpen,
  } = useTacticCard(tactic);

  return (
    <>
      <Card key={ tactic._id } variant="outlined" sx={ CustomContentCardStyle }>
        <Box display={ 'flex' }>
          <Box sx={ getStyleFromCategory(getCategoryForChannel(tactic.channel), tactic.channel) }>
            { getCategoryForChannel(tactic.channel) }
          </Box>
          {
            readOnly ? null
              : <Box sx={ getStyleFromVeevaSyncStatus(tactic.veevaSyncStatus as VeevaSyncStatus) }>
                { tactic.veevaSyncStatus }
              </Box>
          }
        </Box>
        <Box marginTop={ YodaSpacing.xxxSmall } paddingLeft={ YodaSpacing.xSmall }>
          <Box sx={ TacticCardTitleStyle } >
            { tactic.title }
          </Box>
          <Box sx={ clickableIdStyle }>
            { `ID: ${tactic.formattedTacticId}` }
            {
              readOnly ? null
                : <IconButton
                  onClick={ () => handleCopy(tactic.formattedTacticId) }
                  icon={ Icons.contentCopy }
                  variant={ ButtonVariant.primary }
                  backgroundType={ ButtonBackgroundType.noBackground }
                />
            }
          </Box>
          <Box sx={ TacticCardFontStyle }>
            { `Channel: ${Channel[tactic.channel as unknown as keyof typeof Channel]}` }
          </Box>
          <Box sx={ TacticCardFontStyle } >
            {
              `Date: ${tactic.dateStart ? new Date(tactic.dateStart).toLocaleDateString() : ''} 
                - ${tactic.dateEnd ? new Date(tactic.dateEnd).toLocaleDateString() : ''}`
            }
          </Box>
        </Box>
        {
          readOnly ? null
            : <Box sx={ iconsStyle } >
              <EditOutlinedIcon color='inherit' onClick={ () => editTacticCallback() } />
              {
                flags.mayaBiogenlinc3701EnableTacticDeleting
                  ? <DeleteOutlineOutlinedIcon onClick={ () => openModalCallback(tactic._id) }/>
                  : null
              }
            </Box>
        }
      </Card>
      {
        open && (
          <ConfirmationDialog
            dialogTitle={ t('maya_tactic_modal_remove') }
            onConfirm={
              () => {
                deleteTacticCallback(tactic._id);
              }
            }
            onConfirmLabel={ removeTacticLoading ? <Loader center size={ 1 } color={ YodaColors.inherit } /> : t('actions_confirm_label') }
            onDismiss={ () => setOpen(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
    </>
  );
};

export default TacticCard;

